import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface ScheduleOperationsQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface ScheduleOperationsOverviewKpis {
  brokenAppointments: string
  noShow: string
  rescheduleRate: string
  sameDayCancellations: string
  appointmentOverruns: string
  lostChairTime: string
  openChairTime: string
  unfilledSlots: string
  waitListPatients: string
  confirmationsPending: string
  newPatientOpenings: string
  hygieneOpenings: string
}

export interface ScheduleOperationsOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: ScheduleOperationsOverviewKpis
  goals: { brokenAppointmentGoalPct: number }
}

export interface ScheduleOperationsDetailKpis {
  brokenAppointmentRate: string
  brokenAppointmentGoal: string
  noShows: number
  rescheduleRate: string
  sameDayCancellations: number
  openChairTime: string
  unfilledSlots: number
  comparisonLabel: string
  brokenRateDelta: string
  brokenRateTrend: 'up' | 'down'
  noShowsDelta: string
  noShowsTrend: 'up' | 'down'
  rescheduleDelta: string
  rescheduleTrend: 'up' | 'down'
  cancellationsDelta: string
  cancellationsTrend: 'up' | 'down'
  openChairDelta: string
  openChairTrend: 'up' | 'down'
  unfilledDelta: string
  unfilledTrend: 'up' | 'down'
}

export interface ScheduleOperationsChartPoint {
  label: string
  filled: number
  disruptions: number
}

export interface ScheduleOperationsChartConfig {
  data: ScheduleOperationsChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface ScheduleOperationsByProviderRow {
  id: string
  provider: string
  scheduled: number
  brokenAppts: number
  noShows: number
  sameDayCancellations: number
  unfilledSlots: number
  lostChairTime: string
}

export interface ScheduleOperationsByTimePeriodRow {
  id: string
  timePeriod: string
  scheduled: number
  disruptions: number
  unfilledSlots: number
  openChairTime: string
  waitListFills: number
}

export interface ScheduleOperationsDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: ScheduleOperationsDetailKpis
  chart: ScheduleOperationsChartConfig
  byProvider: ScheduleOperationsByProviderRow[]
  byTimePeriod: ScheduleOperationsByTimePeriodRow[]
}

const scheduleOperationsGet = <T>(
  path: string,
  params: ScheduleOperationsQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/operations/schedule-operations/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getScheduleOperationsOverview = (
  params: ScheduleOperationsQueryParams,
  options?: { signal?: AbortSignal },
): Promise<ScheduleOperationsOverviewResult> =>
  scheduleOperationsGet('overview', params, options)

export const getScheduleOperationsDetail = (
  params: ScheduleOperationsQueryParams,
  options?: { signal?: AbortSignal },
): Promise<ScheduleOperationsDetailResult> =>
  scheduleOperationsGet('detail', params, options)
